import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getAugmentDisplayName, getAugmentStyleChoicesForPool } from '../utils/augments';
import './AugmentPoolStyleDot.css';

/**
 * Small corner control on a pool augment card — opens a compact art picker.
 */
export function AugmentPoolStyleDot({
  poolValue,
  inventory,
  selectedStyleId,
  onSelect,
  disabled = false
}) {
  const dotRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const choices = getAugmentStyleChoicesForPool(poolValue, inventory);
  const hasChoices = choices.length > 1;
  const hasCustomStyle = selectedStyleId != null && selectedStyleId !== undefined;
  const displayName = getAugmentDisplayName(poolValue);

  useLayoutEffect(() => {
    if (!hasChoices) return;
    if (!open || !dotRef.current || !menuRef.current) return;
    const dot = dotRef.current.getBoundingClientRect();
    const menu = menuRef.current.getBoundingClientRect();
    const pad = 8;
    let top = dot.bottom + 6;
    let left = dot.left;
    if (top + menu.height > window.innerHeight - pad) {
      top = dot.top - menu.height - 6;
    }
    left = Math.max(pad, Math.min(left, window.innerWidth - menu.width - pad));
    top = Math.max(pad, Math.min(top, window.innerHeight - menu.height - pad));
    setPos({ top, left });
  }, [open, choices.length, hasChoices]);

  useEffect(() => {
    if (!hasChoices || !open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e) => {
      if (dotRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [open, hasChoices]);

  if (!hasChoices) return null;

  const handleDotClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) return;
    setOpen((v) => !v);
  };

  const handleSelect = (styleId) => {
    onSelect(styleId);
    setOpen(false);
  };

  return (
    <>
      <button
        ref={dotRef}
        type="button"
        className={`augment-style-dot${hasCustomStyle ? ' augment-style-dot--custom' : ''}${open ? ' augment-style-dot--open' : ''}`}
        aria-label={`Change art for ${displayName}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        disabled={disabled}
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        onClick={handleDotClick}
      />
      {open
        ? ReactDOM.createPortal(
            <div
              ref={menuRef}
              className="augment-style-menu"
              style={{ top: pos.top, left: pos.left }}
              role="dialog"
              aria-label={`Art for ${displayName}`}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="augment-style-menu__grid" role="listbox">
                {choices.map((choice) => {
                  const isSelected =
                    (choice.styleId == null &&
                      (selectedStyleId == null || selectedStyleId === undefined)) ||
                    choice.styleId === selectedStyleId;
                  return (
                    <button
                      key={choice.styleId ?? 'default'}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      title={choice.name}
                      className={`augment-style-menu__option${isSelected ? ' augment-style-menu__option--selected' : ''}`}
                      onClick={() => handleSelect(choice.styleId)}
                    >
                      <img src={choice.svg} alt="" draggable={false} />
                    </button>
                  );
                })}
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
